import React from "react";
import { useAppDispatch } from "@SonarCX/hooks/redux";
import { useHistory } from "react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { Calendar2, People, TickCircle } from "iconsax-react";
import { orderBy } from "lodash";

import Table from "../../../../Table";
import Header from "../../../../Table/Header";
import UsersList from "../../../../../../pages/Tasks/Cells/UsersList";
import DueDateComponent from "../../../../../../pages/Tasks/Cells/DueDateComponent";
import Status from "../../../../../../pages/Tasks/Cells/Status";
import SubtaskTitle from "../../../../../../pages/Tasks/Cells/SubtaskTitle";
import SubtaskMobileDefault from "../../../../../../pages/Tasks/Cells/SubtaskMobileDefault";

import { Routes } from "@SonarCX/Routes";
import { shiftValue } from "@SonarCX/utilities";
import { AddErrorAlert, AddSuccessAlert } from "@SonarCX/slices/alert";
import { useTaskList } from "@SonarCX/providers/TaskListProvider";
import { useTaskAPI } from "@SonarCX/providers/TaskAPIProvider";
import { useBreakpoint } from "@SonarCX/providers/BreakpointProvider";
import useDetectNihalife from "@SonarCX/hooks/useDetectInstance/useDetectNihalife";
import useStyles from "./styles";

const TABLE_ID = "subtasks_table";
const columnHelper = createColumnHelper<APITask>();

export type SubtasksProps = {
  existingSubtasks: null | APITask[];
  setPending: React.Dispatch<React.SetStateAction<string[]>>;
  pendingDelete: string[];
  queueForDelete: (i: string) => void;
  setExistingSubtasks: CallableFunction;
  showClosedTask?: boolean;
};

const Subtasks: React.FC<SubtasksProps> = ({
  existingSubtasks,
  setPending,
  pendingDelete,
  queueForDelete,
  setExistingSubtasks,
  showClosedTask = true,
}) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { isSmall } = useBreakpoint();

  const { pushFrame } = useTaskList();
  const { patchTasks } = useTaskAPI();
  const { isNihalife } = useDetectNihalife();

  const [existing, setExisting] = React.useState(existingSubtasks ?? []);
  const sortedTasks: APITask[] = React.useMemo(() => {
    if (!isNihalife) {
      return existing.sort((a, b) => a.sort_order - b.sort_order);
    }

    // NIHALIFE instance requires sorting by due date instead of sort_order
    return orderBy(existing, ["completed_at", "due_date"], ["desc", "asc"]);
  }, [isNihalife, existing]);
  const visibleTasks = React.useMemo(() => {
    if (showClosedTask) {
      return sortedTasks;
    }

    return sortedTasks.filter((task) => !task.closed_at);
  }, [showClosedTask, sortedTasks]);

  React.useEffect(() => {
    setExisting(existingSubtasks ?? []);
  }, [existingSubtasks]);

  const classes = useStyles();

  const subtaskRedirect = React.useCallback(
    (task: APITask) => {
      pushFrame(
        visibleTasks.map((st) => st.id),
        task.id,
      );

      // delete task from pending delete list
      setPending((pending) => pending.filter((id) => id !== task.id));
      history.push(`${Routes.TASKS}/${task.id}`);
    },
    [pushFrame, visibleTasks, setPending, history],
  );

  const handleClickRow = React.useCallback(
    (row) => {
      const taskId = row.original.id;
      pushFrame(
        visibleTasks.map((st) => st.id),
        taskId,
      );

      // delete task from pending delete list
      setPending((pending) => pending.filter((id) => id !== taskId));
      history.push(`${Routes.TASKS}/${taskId}`);
    },
    [pushFrame, visibleTasks, setPending, history],
  );
  // Moves a task from one index to another
  const handleMove = React.useCallback(
    (oldIndex: number, newIndex: number) => {
      // Make sure new index is possible in sortedTasks
      const clampedNewIndex = Math.min(
        Math.max(newIndex, 0),
        sortedTasks.length - 1,
      );

      // Move the element in the sorted array
      const moved = shiftValue(
        sortedTasks,
        oldIndex,
        clampedNewIndex,
      ) as APITask[];

      const toUpdate: Pick<APITask, "id" | "sort_order">[] = [];
      // Assign new values for sort order
      const newSort = moved.map((task, i) => {
        const newSort = i * 10;
        const newTask: APITask = { ...task, sort_order: newSort };
        toUpdate.push({ id: task.id, sort_order: newSort });
        return newTask;
      });

      // Update locally
      setExisting(newSort);

      // Update in api
      patchTasks(toUpdate)
        .then(() => {
          dispatch(AddSuccessAlert("Changes saved", 1500));
          //update and save order
          setExistingSubtasks(newSort);
        })
        .catch((err) => dispatch(AddErrorAlert({ err })));
    },
    [sortedTasks, patchTasks, dispatch, setExistingSubtasks],
  );

  const columns = React.useMemo(() => {
    if (isSmall) {
      // only showing one column on small screens
      const titleColumn = columnHelper.accessor("title", {
        header: () => <Header>Subtask</Header>,
        enableSorting: false,
        cell: ({ row }) => (
          <SubtaskMobileDefault
            task={row.original}
            taskClick={subtaskRedirect}
          />
        ),
      });

      return [titleColumn];
    }

    const titleColumn = columnHelper.accessor("title", {
      header: () => <Header>Subtask</Header>,
      enableSorting: false,
      cell: ({ row }) => (
        <SubtaskTitle
          task={row.original}
          taskClick={subtaskRedirect}
          mobile={isSmall}
          queueForDelete={queueForDelete}
          isDeleting={pendingDelete.includes(row.original.id)}
        />
      ),
    });

    const assignColumn = columnHelper.accessor("assignee_ids", {
      header: () => <Header Icon={People}>Assignees</Header>,
      enableSorting: false,
      cell: ({ row }) => {
        const task = row.original;
        const assignees = [...(task?.assignee_ids ?? [])];
        return (
          <UsersList
            prop="assignee_ids"
            taskID={task.id}
            list={[...new Set(assignees)]}
            observerId={TABLE_ID}
          />
        );
      },
    });

    const dueColumn = columnHelper.accessor("due_date", {
      header: () => <Header Icon={Calendar2}>Due</Header>,
      enableSorting: false,
      cell: ({ row }) => <DueDateComponent task={row.original} />,
    });

    const statusColumn = columnHelper.accessor("status_id", {
      header: () => <Header Icon={TickCircle}>Status</Header>,
      enableSorting: false,
      cell: ({ row }) => <Status task={row.original} />,
    });

    return [titleColumn, dueColumn, statusColumn, assignColumn].filter(Boolean);
    //adding queueForDelete triggers infinity.
    //eslint-disable-next-line
  }, [isSmall, subtaskRedirect, pendingDelete]);

  return (
    <div className={classes.root}>
      <Table
        id={TABLE_ID}
        data={visibleTasks}
        columns={columns}
        onRowClicked={handleClickRow}
        message="There are no subtasks"
        useManualSort={true}
        disableSortRemove={true}
        // UI
        isSmall={isSmall}
        padBottom={isSmall}
        disableHeader={isSmall}
        //sort
        allowDragDropSort={!isNihalife}
        dragDropID="SUB_TASK"
        onDragDrop={handleMove}
        alternateDropCallback
        pagesize={Math.max(existingSubtasks?.length ?? 0, 100) * 2}
      />
    </div>
  );
};

export default Subtasks;
